import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  listNotifications,
  unreadCount as fetchUnread,
  markRead,
  markReadAll,
  dismissNotification,
} from './notificationApi';
import {
  isNotificationSoundEnabled,
  playNotificationSound,
  setNotificationSoundEnabled,
  unlockNotificationAudio,
} from './notificationSound';
import { getAccessToken } from '../../services/authService';
import { apiUrl } from '../../config';
import './notifications.css';

function timeAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

class NotificationBell extends Component {
  state = {
    open: false,
    items: [],
    count: 0,
    loading: false,
    soundOn: isNotificationSoundEnabled(),
  };

  _pollTimer = null;
  _sseController = null;
  _sseCancelled = false;

  componentDidMount() {
    this.refresh();
    this._pollTimer = setInterval(() => this.refresh(), 60000);
    this.startRealtime();
  }

  componentWillUnmount() {
    if (this._pollTimer) clearInterval(this._pollTimer);
    this.stopRealtime();
  }

  refresh = async () => {
    try {
      const [rows, n] = await Promise.all([
        listNotifications({ limit: 20 }),
        fetchUnread(),
      ]);
      this.setState({ items: rows, count: n });
    } catch (_) {
      /* ignore */
    }
  };

  startRealtime = () => {
    const token = getAccessToken();
    if (!token) return;

    this.stopRealtime();
    this._sseCancelled = false;
    const controller = new AbortController();
    this._sseController = controller;

    const base = String(apiUrl || '').replace(/\/$/, '');
    const url = `${base}/realtime/stream?topics=${encodeURIComponent('notifications')}`;

    (async () => {
      try {
        const res = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            'x-auth-token': token,
            Accept: 'text/event-stream',
          },
          signal: controller.signal,
        });
        if (!res.ok || !res.body) return;
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        while (!this._sseCancelled) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split('\n\n');
          buffer = parts.pop() || '';
          for (let i = 0; i < parts.length; i += 1) {
            this.handleSseChunk(parts[i]);
          }
        }
      } catch (_) {
        /* aborted or network */
      }
    })();
  };

  stopRealtime = () => {
    this._sseCancelled = true;
    if (this._sseController) {
      this._sseController.abort();
      this._sseController = null;
    }
  };

  handleSseChunk = (chunk) => {
    const lines = chunk.split('\n');
    let data = '';
    for (let i = 0; i < lines.length; i += 1) {
      if (lines[i].startsWith('data:')) data += lines[i].slice(5).trim();
    }
    if (!data) return;
    let parsed;
    try {
      parsed = JSON.parse(data);
    } catch (_) {
      return;
    }
    const payload = parsed.data || parsed;
    if (payload.type === 'notification.created' && payload.notification) {
      const n = payload.notification;
      this.setState((prev) => ({
        items: [n, ...prev.items].slice(0, 30),
        count: prev.count + 1,
      }));
      playNotificationSound({ severity: n.severity });
      if (n.severity === 'critical' || n.severity === 'warning') {
        const toastFn = n.severity === 'critical' ? toast.error : toast.warn;
        toastFn(n.title, {
          onClick: () => {
            if (n.url) {
              const path = n.url.startsWith('/') ? n.url : `/${n.url}`;
              window.location.href = `/client${path}`;
            }
          },
        });
      }
    }
  };

  onOpen = async () => {
    unlockNotificationAudio();
    const willOpen = !this.state.open;
    this.setState({ open: willOpen });
    if (willOpen) {
      this.setState({ loading: true });
      await this.refresh();
      this.setState({ loading: false });
    }
  };

  toggleSound = (e) => {
    e.preventDefault();
    e.stopPropagation();
    unlockNotificationAudio();
    const next = !this.state.soundOn;
    setNotificationSoundEnabled(next);
    this.setState({ soundOn: next });
    if (next) playNotificationSound({ severity: 'info' });
  };

  onClickItem = async (n) => {
    try {
      await markRead([n.id]);
      this.setState((prev) => ({
        items: prev.items.map((x) =>
          x.id === n.id ? { ...x, readAt: new Date().toISOString() } : x
        ),
        count: Math.max(0, prev.count - (n.readAt ? 0 : 1)),
        open: false,
      }));
    } catch (_) {
      this.setState({ open: false });
    }
  };

  onDismiss = async (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    await dismissNotification(id);
    this.setState((prev) => ({
      items: prev.items.filter((x) => x.id !== id),
    }));
    await this.refresh();
  };

  onReadAll = async () => {
    await markReadAll();
    await this.refresh();
  };

  render() {
    const { open, items, count, loading, soundOn } = this.state;

    return (
      <div className="navbar-action position-relative notification-bell">
        <button
          type="button"
          className="navbar-icon-button notification-bell__button"
          onClick={this.onOpen}
          aria-label="Notifications"
          title="Notifications"
        >
          <i className="fa fa-bell" />
          {count > 0 && (
            <span className="badge badge-danger notification-bell__badge">
              {count > 99 ? '99+' : count}
            </span>
          )}
        </button>
        {open && (
          <div className="dropdown-menu dropdown-menu-right show notification-bell__panel">
            <div className="d-flex justify-content-between align-items-center px-3 py-2 notification-bell__header">
              <strong className="small">Notifications</strong>
              <div>
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 mr-2"
                  onClick={this.toggleSound}
                  title={soundOn ? 'Mute notification sound' : 'Enable notification sound'}
                  aria-label={soundOn ? 'Mute notification sound' : 'Enable notification sound'}
                >
                  <i className={`fa fa-volume-${soundOn ? 'up' : 'off'}`} aria-hidden />
                </button>
                <button type="button" className="btn btn-link btn-sm p-0 mr-2" onClick={this.onReadAll}>
                  Read all
                </button>
                <Link
                  className="btn btn-link btn-sm p-0"
                  to="/notifications"
                  onClick={() => this.setState({ open: false })}
                >
                  Center
                </Link>
              </div>
            </div>
            {loading && (
              <div className="px-3 py-2 small notification-bell__loading">Loading…</div>
            )}
            {!loading && !items.length && (
              <div className="px-3 py-3 small notification-bell__empty">No notifications</div>
            )}
            {items.map((n) => (
              <div key={n.id} className="notification-bell__item px-0">
                <Link
                  to={n.url || '/notifications'}
                  className={`dropdown-item small notification-bell__link${
                    n.readAt ? '' : ' notification-bell__link--unread'
                  }`}
                  onClick={() => this.onClickItem(n)}
                >
                  <div className="d-flex justify-content-between">
                    <span className="notification-bell__title">{n.title}</span>
                    <span className="notification-bell__meta ml-2">{timeAgo(n.createdAt)}</span>
                  </div>
                  {n.body && (
                    <div className="notification-bell__body text-truncate">{n.body}</div>
                  )}
                </Link>
                <button
                  type="button"
                  className="btn btn-link btn-sm py-0 px-3 notification-bell__dismiss"
                  onClick={(e) => this.onDismiss(e, n.id)}
                >
                  Dismiss
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
}

export default NotificationBell;
